import aesjs from 'aes-js';

/**
 * Decrypt MP4 AES-128-ECB per-sample-block (mis. DramaBox) di client.
 * Port dari player reference resmi QuickPlay (dramabox_player.html).
 *
 * DramaBox mengenkripsi tiap sample sepanjang `blockLen = size - size%16`
 * dengan AES-128-ECB. Tidak ada senc/IV — cukup parse offset+size sample
 * dari stbl, decrypt bagian blok-16-nya. Sisa byte (< 16) dibiarkan.
 * Tidak perlu neutralize box karena container-nya MP4 biasa (bukan CENC).
 */

function readU32(b: Uint8Array, o: number): number {
  return ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
}
function readU64(b: Uint8Array, o: number): number {
  return readU32(b, o) * 0x100000000 + readU32(b, o + 4);
}
function readTag(b: Uint8Array, o: number): string {
  return String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);
}

interface Box {
  offset: number;
  size: number;
  tag: string;
  bodyOff: number;
}

function iterBoxes(buf: Uint8Array, s: number, e: number): Box[] {
  const boxes: Box[] = [];
  let off = s;
  while (off + 8 <= e) {
    const sz = readU32(buf, off);
    if (sz < 8 || off + sz > e) break;
    boxes.push({ offset: off, size: sz, tag: readTag(buf, off + 4), bodyOff: off + 8 });
    off += sz;
  }
  return boxes;
}

function findBox(buf: Uint8Array, s: number, e: number, tag: string): Box | null {
  for (const b of iterBoxes(buf, s, e)) if (b.tag === tag) return b;
  return null;
}

function walkPath(buf: Uint8Array, s: number, e: number, ...tags: string[]): Box | null {
  let cs = s;
  let ce = e;
  let found: Box | null = null;
  for (const tag of tags) {
    found = null;
    for (const b of iterBoxes(buf, cs, ce)) {
      if (b.tag === tag) {
        found = b;
        cs = b.bodyOff;
        ce = b.offset + b.size;
        break;
      }
    }
    if (!found) return null;
  }
  return found;
}

interface Track {
  sampleOffs: number[];
  sizes: number[];
}

function parseSampleTable(buf: Uint8Array, ss: number, se: number): Track | null {
  const find = (t: string) => findBox(buf, ss, se, t);
  const stsz = find('stsz');
  const stsc = find('stsc');
  const stco = find('stco');
  const co64 = find('co64');
  if (!stsz || !stsc || (!stco && !co64)) return null;

  const defSz = readU32(buf, stsz.bodyOff + 4);
  const n = readU32(buf, stsz.bodyOff + 8);
  const sizes = new Array<number>(n);
  if (defSz === 0) {
    for (let i = 0; i < n; i++) sizes[i] = readU32(buf, stsz.bodyOff + 12 + i * 4);
  } else {
    sizes.fill(defSz);
  }

  let chunkOffs: number[];
  if (stco) {
    const c = readU32(buf, stco.bodyOff + 4);
    chunkOffs = new Array<number>(c);
    for (let i = 0; i < c; i++) chunkOffs[i] = readU32(buf, stco.bodyOff + 8 + i * 4);
  } else {
    const c = readU32(buf, co64!.bodyOff + 4);
    chunkOffs = new Array<number>(c);
    for (let i = 0; i < c; i++) chunkOffs[i] = readU64(buf, co64!.bodyOff + 8 + i * 8);
  }

  const ne = readU32(buf, stsc.bodyOff + 4);
  const entries: { firstChunk: number; spc: number }[] = [];
  for (let i = 0; i < ne; i++) {
    entries.push({
      firstChunk: readU32(buf, stsc.bodyOff + 8 + i * 12),
      spc: readU32(buf, stsc.bodyOff + 12 + i * 12),
    });
  }

  const sampleOffs: number[] = [];
  let si = 0;
  for (let ci = 0; ci < chunkOffs.length && si < n; ci++) {
    let spc = 1;
    for (const e of entries) {
      if (ci + 1 >= e.firstChunk) spc = e.spc;
      else break;
    }
    let cur = chunkOffs[ci]!;
    for (let k = 0; k < spc && si < n; k++) {
      sampleOffs.push(cur);
      cur += sizes[si]!;
      si++;
    }
  }
  return { sampleOffs, sizes };
}

function parseAllTracks(buf: Uint8Array, moov: Box): Track[] {
  const tracks: Track[] = [];
  for (const trak of iterBoxes(buf, moov.bodyOff, moov.offset + moov.size)) {
    if (trak.tag !== 'trak') continue;
    const mdia = walkPath(buf, trak.bodyOff, trak.offset + trak.size, 'mdia');
    if (!mdia) continue;
    const hdlr = walkPath(buf, mdia.bodyOff, mdia.offset + mdia.size, 'hdlr');
    if (!hdlr) continue;
    const h = readTag(buf, hdlr.offset + 16);
    if (h !== 'vide' && h !== 'soun') continue;
    const stbl = walkPath(buf, mdia.bodyOff, mdia.offset + mdia.size, 'minf', 'stbl');
    if (!stbl) continue;
    const t = parseSampleTable(buf, stbl.bodyOff, stbl.offset + stbl.size);
    if (t) tracks.push(t);
  }
  return tracks;
}

/**
 * Decrypt full MP4 (AES-128-ECB per sample) in-place & return blob URL.
 * `keyHex` = 32-char hex atau string 16-char (kompat dgn player docs).
 */
export function decryptMp4EcbToBlobUrl(
  encrypted: ArrayBuffer,
  keyHex: string,
): string {
  const buf = new Uint8Array(encrypted);
  let keyBytes: Uint8Array;
  if (keyHex.length === 32 && /^[0-9a-fA-F]+$/.test(keyHex)) {
    keyBytes = new Uint8Array(keyHex.match(/.{2}/g)!.map((h) => Number.parseInt(h, 16)));
  } else if (keyHex.length === 16) {
    keyBytes = new TextEncoder().encode(keyHex);
  } else {
    throw new Error('format key tidak valid (harus 32 hex atau 16 char)');
  }

  const moov = walkPath(buf, 0, buf.length, 'moov');
  if (!moov) throw new Error("box 'moov' tidak ditemukan");
  const tracks = parseAllTracks(buf, moov);
  if (tracks.length === 0) throw new Error('tidak ada track ditemukan');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ecb = new (aesjs as any).ModeOfOperation.ecb(Array.from(keyBytes));
  for (const t of tracks) {
    for (let i = 0; i < t.sampleOffs.length; i++) {
      const off = t.sampleOffs[i]!;
      const sz = t.sizes[i]!;
      const blockLen = sz - (sz % 16);
      if (blockLen > 0) {
        const enc = buf.slice(off, off + blockLen);
        const dec = ecb.decrypt(enc);
        buf.set(dec, off);
      }
    }
  }
  const blob = new Blob([buf], { type: 'video/mp4' });
  return URL.createObjectURL(blob);
}
